import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, FileText, Download, Loader2 } from "lucide-react";
import Button from "../../components/common/ui/button/Button";
import { apiClient } from "../../utils/apiClient";
import { downloadFromResponse } from "../../utils/fileDownload";
import type {
  FormField,
  FormSubmissionResponseDto,
} from "@secure-caseworker/shared";

type UploadedFile = {
  id: string;
  name: string;
  size: number;
};

interface FormSubmissionViewPageProps {
  shareToken?: string;
}

export const FormSubmissionViewPage: React.FC<FormSubmissionViewPageProps> = ({
  shareToken,
}) => {
  const { submissionId, token: routeToken } = useParams<{
    submissionId?: string;
    token?: string;
  }>();

  // Use shareToken prop if provided, otherwise fall back to route token
  const token = shareToken || routeToken;
  const navigate = useNavigate();
  const location = useLocation();

  const [submission, setSubmission] =
    useState<FormSubmissionResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFileIds, setDownloadingFileIds] = useState<Set<string>>(
    new Set(),
  );

  // Track which resource we've loaded to prevent double-loading in StrictMode
  const loadedResourceRef = React.useRef<string | null>(null);

  // Check if this is a public view - either /share/* or /share/forms/*
  const isPublicView = location.pathname.includes("/share");

  useEffect(() => {
    const resourceKey = token || submissionId;

    // Don't load if we don't have a resource identifier
    if (!resourceKey) {
      setLoading(false);
      return;
    }

    // Prevent duplicate loads in React StrictMode
    if (loadedResourceRef.current === resourceKey) {
      return;
    }

    loadedResourceRef.current = resourceKey;

    const loadSubmission = async () => {
      try {
        setLoading(true);
        setError(null);

        let data;
        if (isPublicView && token) {
          // Public view via share link - verification is handled server-side
          data = await apiClient.getFormSubmissionByToken(token);
        } else if (submissionId) {
          // Authenticated view
          data = await apiClient.getFormSubmission(submissionId);
        }

        if (!data) {
          throw new Error("Failed to load form submission");
        }

        setSubmission(data);
      } catch (err: any) {
        setError(err.message || "Failed to load submission");
      } finally {
        setLoading(false);
      }
    };

    loadSubmission();
  }, [submissionId, token, isPublicView]);

  const handleFileDownload = async (file: UploadedFile) => {
    setDownloadingFileIds((prev) => new Set(prev).add(file.id));
    try {
      // Use public endpoint for share links, authenticated endpoint otherwise
      const response =
        isPublicView && token
          ? await apiClient.downloadFormSubmissionFile(token, file.id)
          : await apiClient.downloadFileResponse(file.id);
      await downloadFromResponse(response, file.name);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloadingFileIds((prev) => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatFieldValue = (field: FormField, value: any) => {
    if (value === null || value === undefined || value === "") {
      return <span className="text-gray-400 italic">No response</span>;
    }

    switch (field.field_type) {
      case "checkbox":
        return value ? "Yes" : "No";
      case "date":
        return new Date(value).toLocaleDateString();
      case "email":
        return (
          <a
            href={`mailto:${value}`}
            className="text-brand-600 hover:underline"
          >
            {value}
          </a>
        );
      case "tel":
        return (
          <a href={`tel:${value}`} className="text-brand-600 hover:underline">
            {value}
          </a>
        );
      case "url":
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 hover:underline"
          >
            {value}
          </a>
        );
      case "textarea":
        return <pre className="whitespace-pre-wrap font-sans">{value}</pre>;
      case "file": {
        const files = value as UploadedFile[];
        if (!Array.isArray(files) || files.length === 0) {
          return <span className="text-gray-400 italic">No files uploaded</span>;
        }
        return (
          <div className="space-y-2">
            {files.map((file) => {
              const isDownloading = downloadingFileIds.has(file.id);
              return (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      ({formatFileSize(file.size)})
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFileDownload(file)}
                    disabled={isDownloading}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-brand-600 hover:text-brand-700 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded transition-colors disabled:opacity-50"
                  >
                    {isDownloading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    <span>{isDownloading ? "Downloading..." : "Download"}</span>
                  </button>
                </div>
              );
            })}
          </div>
        );
      }
      default:
        return String(value);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading submission...
          </p>
        </div>
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600 dark:text-gray-400">
            {error || "Submission not found"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          {!isPublicView && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              startIcon={<ArrowLeft className="h-4 w-4" />}
              className="mb-4"
            >
              Back
            </Button>
          )}

          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-brand-100 dark:bg-brand-900/20 rounded-lg">
                <FileText className="h-8 w-8 text-brand-600" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {submission.formTemplateTitle}
                </h1>
                <div className="flex gap-6 text-sm text-gray-500 dark:text-gray-400">
                  <div>
                    <span className="font-medium">Submitted:</span>{" "}
                    {new Date(submission.submittedAt).toLocaleString()}
                  </div>
                  {!isPublicView && (
                    <div>
                      <span className="font-medium">By:</span>{" "}
                      {submission.submittedBy || "Anonymous"}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isPublicView && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                  Shared via secure link
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Form Fields */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
              Submission Details
            </h2>

            <div className="space-y-6">
              {submission.formFields.map((field) => (
                <div
                  key={field.id}
                  className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0 last:pb-0"
                >
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {field.label}
                      {field.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    {field.field_type !== "text" && field.field_type !== "textarea" && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        ({field.field_type})
                      </span>
                    )}
                  </div>
                  <div className="text-gray-900 dark:text-gray-100">
                    {formatFieldValue(field, submission.fieldValues[field.id])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Print-friendly footer */}
        <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400 print:block hidden">
          <p>
            This is a secure form submission from your case management system
          </p>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white; }
          .print\\:block { display: block !important; }
          button { display: none !important; }
        }
      `}</style>
    </div>
  );
};
